import type { Occasion, OccasionIdInput } from './occasions.types'

type OptionalOccasionId = OccasionIdInput | null | undefined

/** Form values carry `occasion_ids`; loaded resources carry `occasions`. */
interface OccasionIdSource {
  occasion_ids?: OptionalOccasionId[] | null | undefined
  occasions?: ({ id?: OptionalOccasionId } | null | undefined)[] | null | undefined
}

const normalizeOccasionIdValues = (occasionIds: OptionalOccasionId[]) =>
  occasionIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)

const normalizeOccasionValueObjects = (
  occasions: NonNullable<OccasionIdSource['occasions']>
): number[] =>
  occasions.map((occasion) => Number(occasion?.id)).filter((id) => Number.isInteger(id) && id > 0)

export const normalizeOccasionIds = (value: OccasionIdSource | null | undefined): number[] => {
  if (Array.isArray(value?.occasion_ids)) {
    return normalizeOccasionIdValues(value.occasion_ids)
  }

  if (Array.isArray(value?.occasions)) {
    return normalizeOccasionValueObjects(value.occasions)
  }

  return []
}

export const normalizeNullableId = (value: OptionalOccasionId): number | null => {
  const id = Number(value)
  return Number.isInteger(id) && id > 0 ? id : null
}

const DATE_BACKED_OCCASION_CALENDAR_ORDER = [
  'new_years_day',
  'valentines_day',
  'international_womens_day',
  'st_patricks_day',
  'easter',
  'passover',
  'administrative_professionals_day',
  'mothers_day',
  'memorial_day',
  'rose_month',
  'fathers_day',
  'fourth_of_july',
  'friendship_day',
  'labor_day',
  'grandparents_day',
  'rosh_hashanah',
  'boss_day',
  'sweetest_day',
  'halloween',
  'thanksgiving',
  'hanukkah',
  'christmas',
  'kwanzaa',
]

const dateBackedOccasionCalendarIndex = new Map(
  DATE_BACKED_OCCASION_CALENDAR_ORDER.map((key, index) => [key, index])
)

/** A holiday schedule option backed by a loaded occasion. */
export interface DateBackedOccasionOption {
  /** The occasion key, e.g. `mothers_day`. */
  id: string
  name: string
  occasionId: number
}

/** Falls back to `fallbackOptions` until the date-backed occasions load. */
export const buildDateBackedOccasionOptions = <TFallbackOption = never>({
  occasions = [],
  fallbackOptions = [],
}: {
  occasions?: Pick<Occasion, 'id' | 'key' | 'label'>[]
  fallbackOptions?: TFallbackOption[]
} = {}): DateBackedOccasionOption[] | TFallbackOption[] => {
  if (!occasions.length) {
    return fallbackOptions
  }

  return occasions
    .map((occasion) => ({
      id: occasion.key,
      name: occasion.label,
      occasionId: Number(occasion.id),
    }))
    .sort((a, b) => {
      const aIndex = dateBackedOccasionCalendarIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER
      const bIndex = dateBackedOccasionCalendarIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER

      if (aIndex !== bIndex) {
        return aIndex - bIndex
      }

      return a.name.localeCompare(b.name)
    })
}

/** A loaded occasion as a select option. */
export interface OccasionSelectOption {
  id: number
  key: string
  kind: Occasion['kind']
  name: string
  sort_order: number
}

/** The selected occasion when it is missing from the loaded options. */
export interface SelectedOccasionFallbackOption {
  id: number
  name: string
}

interface SelectedOccasion {
  id?: OptionalOccasionId
  label?: string | null | undefined
  name?: string | null | undefined
}

/** The leading "no occasion" choice. */
export interface OccasionSelectEmptyOption {
  id: number | string
  name: string
}

export type OccasionSelectChoice =
  | OccasionSelectEmptyOption
  | OccasionSelectOption
  | SelectedOccasionFallbackOption

export const buildOccasionSelectOptions = ({
  occasions = [],
  selectedOccasionId = null,
  selectedOccasion = null,
  emptyOption = { id: '', name: 'No occasion' },
}: {
  occasions?: Pick<Occasion, 'id' | 'key' | 'kind' | 'label' | 'sort_order'>[]
  selectedOccasionId?: OptionalOccasionId
  selectedOccasion?: SelectedOccasion | null | undefined
  emptyOption?: OccasionSelectEmptyOption
} = {}): OccasionSelectChoice[] => {
  const selectedId = normalizeNullableId(selectedOccasionId ?? selectedOccasion?.id)
  const options: OccasionSelectChoice[] = [
    emptyOption,
    ...occasions.map((occasion) => ({
      id: Number(occasion.id),
      key: occasion.key,
      kind: occasion.kind,
      name: occasion.label,
      sort_order: occasion.sort_order,
    })),
  ]

  if (selectedId && !options.some((option) => Number(option.id) === selectedId)) {
    options.push({
      id: selectedId,
      name: selectedOccasion?.label ?? selectedOccasion?.name ?? 'Selected occasion',
    })
  }

  return options
}
